import api, { unwrap, tokenStore } from "../services/platformClient";

/**
 * Normalisasi respons berpaginasi backend { items, pagination:{total} }
 * menjadi { rows, total } yang dipakai komponen tabel admin.
 */
async function paginated(promise) {
  const d = await unwrap(promise);
  if (Array.isArray(d)) return { rows: d, total: d.length };
  return { rows: d.items || [], total: d.pagination?.total ?? (d.items || []).length };
}

/**
 * API LAYER khusus ADMIN DASHBOARD.
 * Memakai axios instance platform yang sama (token + auto-refresh) dari
 * services/platformClient. Semua endpoint di bawah dijaga role di backend
 * (view/monitor: manajerial; users/roles/employees/audit: SUPER_ADMIN/HR_ADMIN).
 */

export const attendanceViewApi = {
  list: (params) => paginated(api.get("/attendance-view", { params })),
  detail: (id) => unwrap(api.get(`/attendance-view/${id}`)),
  // Grid karyawan × tanggal { employees, records, leaves, pagination } (maks 62 hari).
  calendar: (params) => unwrap(api.get("/attendance-view/calendar", { params })),
  // Kartu ringkasan list: { current, previous } (periode pembanding sepanjang sama).
  summary: (params) => unwrap(api.get("/attendance-view/summary", { params })),
  // Dashboard Ringkasan: kondisi tim hari ini (di-scope downline manajer/PIC).
  today: () => unwrap(api.get("/attendance-view/today")),
  departments: async () => (await unwrap(api.get("/attendance-view/departments")))?.items || [],
  // Hapus absensi bermasalah (SUPER_ADMIN/HR_ADMIN). Data di Odoo tidak ikut terhapus.
  remove: (id) => unwrap(api.delete(`/attendance-view/${id}`)),
};

/**
 * MONITOR ANOMALI ABSENSI (pengganti approval). Read-only: daftar anomali
 * (lupa checkin/checkout, double) + status sync terakhir dari Odoo.
 * `syncStatus` hanya untuk SUPER_ADMIN/HR_ADMIN (backend membatasi role) —
 * pemanggilan di-catch di UI agar manajer/PIC tetap bisa lihat daftar anomali.
 */
export const attendanceAnomalyApi = {
  list: async () => (await unwrap(api.get("/attendance-view/anomalies")))?.items || [],
  detail: (id) => unwrap(api.get(`/attendance-view/${id}`)),
  syncStatus: () => unwrap(api.get("/sync/attendance/status")),
};

export const leaveApprovalApi = {
  list: () => unwrap(api.get("/leave/approval")),
  detail: (id) => unwrap(api.get(`/leave/approval/${id}`)),
  approve: (id, payload = {}) => unwrap(api.post(`/leave/approval/${id}/approve`, payload)),
  reject: (id, payload) => unwrap(api.post(`/leave/approval/${id}/reject`, payload)),
};

export const usersApi = {
  list: (params) => paginated(api.get("/users", { params })),
  detail: (id) => unwrap(api.get(`/users/${id}`)),
  create: (payload) => unwrap(api.post("/users", payload)),
  resetPassword: (id, new_password) =>
    unwrap(api.post(`/users/${id}/reset-password`, new_password ? { new_password } : {})),
  setStatus: (id, status) => unwrap(api.patch(`/users/${id}/status`, { status })),
  setRole: (id, role) => unwrap(api.patch(`/users/${id}/role`, { role })),
  setManager: (id, manager_id) => unwrap(api.patch(`/users/${id}/manager`, { manager_id })),
  setPic: (id, pic_id) => unwrap(api.patch(`/users/${id}/pic`, { pic_id })),
  subordinates: (id) => unwrap(api.get(`/users/${id}/subordinates`)),
};

export const rolesApi = {
  list: () => unwrap(api.get("/roles")),
  create: (payload) => unwrap(api.post("/roles", payload)),
  update: (id, payload) => unwrap(api.patch(`/roles/${id}`, payload)),
  remove: (id) => unwrap(api.delete(`/roles/${id}`)),
};

export const employeesApi = {
  list: (params) => paginated(api.get("/employees", { params })),
  detail: (id) => unwrap(api.get(`/employees/${id}`)),
  create: (payload) => unwrap(api.post("/employees", payload)),
  update: (id, payload) => unwrap(api.patch(`/employees/${id}`, payload)),
  assignUser: (id, user_id) => unwrap(api.post(`/employees/${id}/assign-user`, { user_id })),
};

export const auditApi = {
  list: (params) => paginated(api.get("/audit-logs", { params })),
};

/**
 * KONFIGURASI WORKING SCHEDULE (SUPER_ADMIN/HR_ADMIN). Jadwal dipetakan dari
 * Odoo saat sync; admin bisa override (locked) + memicu recompute aturan absensi.
 */
export const workScheduleApi = {
  list: (params) => paginated(api.get("/work-schedules", { params })),
  detail: (id) => unwrap(api.get(`/work-schedules/${id}`)),
  update: (id, payload) => unwrap(api.patch(`/work-schedules/${id}`, payload)),
  recompute: (payload) => unwrap(api.post("/work-schedules/recompute", payload)),
};

/**
 * MAINTENANCE / PANTAU SERVER (SUPER_ADMIN/HR_ADMIN). Status file log +
 * trigger hapus arsip log lama (< hari ini).
 */
export const maintenanceApi = {
  logsStatus: () => unwrap(api.get("/maintenance/logs/status")),
  // after (byte offset) opsional → hanya ambil baris baru sejak posisi itu (hemat).
  tailLogs: ({ lines = 200, after } = {}) =>
    unwrap(api.get("/maintenance/logs/tail", { params: after !== undefined ? { lines, after } : { lines } })),
  cleanupLogs: () => unwrap(api.post("/maintenance/logs/cleanup")),
};

/**
 * FEEDBACK / KOTAK MASUK (SUPER_ADMIN/HR_ADMIN). Kritik, saran & laporan bug
 * dari user beserta lampiran screenshot.
 */
export const feedbackApi = {
  list: (params) => paginated(api.get("/feedback", { params })),
  unreadCount: () => unwrap(api.get("/feedback/unread-count")),
  updateStatus: (id, status) => unwrap(api.patch(`/feedback/${id}/status`, { status })),
};

export { dashboardAPI } from "../services/api";
export { errorMessage, tokenStore } from "../services/platformClient";

/** Tambahkan token ke URL file agar bisa dipakai di <img src> (auth via query). */
export function withToken(url) {
  if (!url) return null;
  const t = tokenStore.getAccess();
  return t ? `${url}${url.includes("?") ? "&" : "?"}token=${encodeURIComponent(t)}` : url;
}
